import React, { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import {
  X, FileText, BarChart2, Loader2, Bug, Sparkles, Zap, LayoutGrid,
  Github, Gitlab, Cloud, GitBranch, GitCommit, GitPullRequest, ExternalLink,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import type { Execution, ITestResult } from '../types';
import { useAuth } from '../context/AuthContext';
import { TerminalView } from './TerminalView';
import { ArtifactsView, type IArtifact } from './ArtifactsView';
import { CreateJiraTicketModal } from './CreateJiraTicketModal';
import { CreateLinearIssueModal } from './CreateLinearIssueModal';
import { CreateMondayItemModal } from './CreateMondayItemModal';
import { AutoBugModal, type IBugReport } from './AutoBugModal';
import { useOrganizationFeatures } from '../hooks/useOrganizationFeatures';

// ── Constants ─────────────────────────────────────────────────────────────────

const isProduction =
  window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
const _viteApiUrl = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '');
const API_URL: string = isProduction ? (_viteApiUrl ?? window.location.origin) : 'http://localhost:3000';

// ── Types ──────────────────────────────────────────────────────────────────────

interface ExecutionDrawerProps {
  executionId: string | null;
  /** Full execution object resolved by the parent — kept live via React Query. */
  execution: Execution | null;
  onClose: () => void;
  /** Tab to activate when the drawer first opens for a given executionId. */
  defaultTab?: DrawerTab;
}

export type DrawerTab = 'terminal' | 'tests' | 'artifacts';

interface ITestAiState {
  status: 'loading' | 'done' | 'error';
  rootCause?: string;
  suggestedFix?: string;
  errorMsg?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ExecutionDrawer({ executionId, execution, onClose, defaultTab }: ExecutionDrawerProps) {
  const { token } = useAuth();
  const { aiFeatures, integrations } = useOrganizationFeatures();
  const queryClient = useQueryClient();
  const [fetchingReport, setFetchingReport] = useState(false);
  const [showJiraModal, setShowJiraModal] = useState(false);
  const [showLinearModal, setShowLinearModal] = useState(false);
  const [showMondayModal, setShowMondayModal] = useState(false);
  const [ciContextOpen, setCiContextOpen] = useState(true);
  const [smartPrOpen, setSmartPrOpen] = useState(true);
  const [generatingBug, setGeneratingBug] = useState(false);
  const [bugReport, setBugReport] = useState<IBugReport | null>(null);
  const [bugError, setBugError] = useState<string | null>(null);
  const [expandedErrors, setExpandedErrors] = useState<Set<string | number>>(new Set());
  const [testAiStates, setTestAiStates] = useState<Record<string | number, ITestAiState>>({});

  // ── Log-history hydration ────────────────────────────────────────────────
  // Fetches the full accumulated log buffer from the server and seeds the
  // React Query cache. Runs on three occasions:
  //   1. Drawer opens for a new executionId (executionId dependency changes).
  //   2. Browser tab regains focus (visibilitychange → 'visible').
  //   3. token changes (e.g. after a silent re-auth).
  //
  // Intentionally not gated on execution.status so that completed executions
  // also re-hydrate correctly when the drawer is reopened. The /logs endpoint
  // returns the Redis live-buffer for RUNNING executions and the MongoDB
  // output field for completed ones — the same call covers both cases.
  //
  // The cache is only updated when the server payload is longer than what is
  // already stored, so an in-flight socket stream is never regressed.
  useEffect(() => {
    if (!executionId || !token) return;

    // Extract the fetch into a named function so both the initial call and
    // the visibilitychange handler can reuse it without duplication.
    const fetchLogs = (): AbortController => {
      const controller = new AbortController();
      axios
        .get(`${API_URL}/api/executions/${executionId}/logs`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        })
        .then(({ data }) => {
          const historical: string = data?.data?.output ?? '';
          if (!historical) return;

          // Seed every matching executions query in the cache. The existing
          // socket handler in useExecutions will continue appending incremental
          // chunks to this pre-hydrated output string — no duplicate connection.
          queryClient.setQueriesData(
            { queryKey: ['executions'], exact: false },
            (old: any) => {
              if (!old?.executions) return old;
              return {
                ...old,
                executions: old.executions.map((ex: Execution) =>
                  ex.taskId === executionId &&
                    ((ex as any).output?.length ?? 0) < historical.length
                    ? { ...ex, output: historical }
                    : ex,
                ),
              };
            },
          );
        })
        .catch(() => {
          // Non-critical: the socket will still stream future log chunks.
        });
      return controller;
    };

    // Initial hydration when the drawer opens.
    let controller = fetchLogs();

    // Re-hydrate whenever the browser tab regains focus. This recovers log
    // chunks that the Socket.io stream may have missed while the tab was hidden.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        controller.abort();
        controller = fetchLogs();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      controller.abort();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [executionId, token, queryClient]);

  // Reset per-test AI results whenever the drawer opens for a different execution.
  useEffect(() => {
    setTestAiStates({});
  }, [executionId]);

  // Store the selected tab alongside the executionId it belongs to.
  // When executionId changes (new row clicked), the ownerId mismatch resets
  // the tab to defaultTab with no extra render cycle — no useEffect needed.
  const [tabState, setTabState] = useState<{ ownerId: string | null; tab: DrawerTab }>({
    ownerId: null,
    tab: defaultTab ?? 'terminal',
  });

  const setActiveTab = (tab: DrawerTab) => setTabState({ ownerId: executionId, tab });

  const isOpen = !!executionId;

  // ── Artifacts query — lifted from ArtifactsView so the drawer can inspect ──
  // results to decide whether to show the "Artifacts" tab at all.
  const TERMINAL_STATUSES = new Set(['PASSED', 'FAILED', 'UNSTABLE', 'ERROR']);
  const { data: artifacts = [], isLoading: artifactsLoading, refetch: refetchArtifacts } = useQuery<IArtifact[]>({
    queryKey: ['artifacts', executionId, token],
    queryFn: async () => {
      const { data } = await axios.get(
        `${API_URL}/api/executions/${executionId}/artifacts`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return data.success ? (data.data.artifacts as IArtifact[]) : [];
    },
    enabled: !!token && !!executionId,
    staleTime: 30_000,
    refetchInterval: () => {
      // Poll every 3 seconds if the parent execution is still running or analyzing
      if (execution?.status === 'RUNNING' || execution?.status === 'ANALYZING') return 3000;
      return false;
    },
  });

  // When the execution transitions to a terminal state, the worker may still be
  // uploading artifacts at that exact moment. Schedule a one-shot refetch after
  // a short delay so any artifacts that land just after completion are captured
  // without requiring a manual page refresh.
  useEffect(() => {
    if (!executionId || !execution?.status) return;
    if (!TERMINAL_STATUSES.has(execution.status)) return;
    const timer = setTimeout(() => { void refetchArtifacts(); }, 2500);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execution?.status, executionId]);

  // ── Dynamic tab list ──────────────────────────────────────────────────────
  // - "Terminal"    — always visible.
  // - "Artifacts"  — visible while loading (to show spinner) OR when results exist.
  // - "AI Analysis"— hidden for PASSED runs that have no analysis data.
  const visibleTabs = useMemo<{ id: DrawerTab; label: string }[]>(() => {
    const tabs: { id: DrawerTab; label: string }[] = [
      { id: 'terminal', label: 'Terminal' },
    ];

    // Phase 5: Show Tests tab when there are results to display.
    const testResults = (execution?.tests ?? []).map((t: any): ITestResult => {
      if (typeof t === 'string') {
        return { testId: t, name: t, status: 'failed' } as ITestResult;
      }
      return t as ITestResult;
    });
    if (testResults.length > 0) {
      tabs.push({ id: 'tests', label: `Tests (${testResults.length})` });
    }

    if (artifactsLoading || artifacts.length > 0) {
      tabs.push({ id: 'artifacts', label: 'Artifacts' });
    }

    return tabs;
  }, [artifactsLoading, artifacts.length, execution?.tests, execution?.status]);

  // ── Derive the effective active tab — fully pure, no effects needed ──────
  // 1. If the stored tab belongs to a different executionId, reset to defaultTab.
  // 2. Clamp the result to visibleTabs so a URL param pointing to a hidden tab
  //    (e.g. ?drawerTab=ai-analysis on a PASSED run) safely falls back to terminal.
  const desiredTab: DrawerTab =
    tabState.ownerId === executionId ? tabState.tab : (defaultTab ?? 'terminal');

  const effectiveTab: DrawerTab = visibleTabs.some((t) => t.id === desiredTab)
    ? desiredTab
    : 'terminal';

  const isFinished = execution && TERMINAL_STATUSES.has(execution.status);
  const LOCAL_INDICATORS = ['localhost', '127.0.0.1', 'host.docker.internal'];
  const isRunLocal = execution ? [
    (execution as any).environment,
    (execution as any).config?.baseUrl,
    (execution as any).baseUrl,
    (execution as any).meta?.baseUrl,
  ].some((v: any) => v && LOCAL_INDICATORS.some((indicator) => v.includes(indicator))) : false;

  const isDashboardCloud = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
  const areReportsInaccessible = isDashboardCloud && isRunLocal;

  const getBaseUrl = () => {
    if ((execution as any)?.reportsBaseUrl) return (execution as any).reportsBaseUrl.replace(/\/$/, '');
    const envBaseUrl = import.meta.env.VITE_API_URL;
    if (envBaseUrl) return envBaseUrl.replace(/\/$/, '');
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:3000' : `https://api.${window.location.hostname}`;
  };

  const baseUrl = getBaseUrl();
  const htmlReportUrl = execution ? `${baseUrl}/${execution.taskId}/native-report/index.html` : '';
  const allureReportUrl = execution ? `${baseUrl}/${execution.taskId}/allure-report/index.html` : '';

  const handleViewReport = async (reportUrl: string) => {
    if (!executionId) return;
    const win = window.open('about:blank', '_blank');
    setFetchingReport(true);
    try {
      const apiBase = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${apiBase}/api/executions/${executionId}/report-token`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const token = json?.data?.token;
      if (!token) throw new Error('No token in response');
      const url = `${reportUrl}?token=${encodeURIComponent(token)}`;
      if (win) win.location.href = url;
      else window.open(url, '_blank');
    } catch (err) {
      console.error('[report-token] Failed:', err);
      if (win) win.location.href = reportUrl;
      else window.open(reportUrl, '_blank');
    } finally {
      setFetchingReport(false);
    }
  };

  // Normalise to uppercase so API responses with mixed case still match
  const executionStatusUpper = execution?.status?.toUpperCase() ?? '';
  const hasTicketIntegration = !!(integrations?.jira?.enabled || integrations?.linear?.enabled || integrations?.monday?.enabled);
  const canGenerateBug =
    aiFeatures.autoBugGeneration &&
    hasTicketIntegration &&
    execution !== null &&
    (executionStatusUpper === 'FAILED' || executionStatusUpper === 'ERROR');

  async function handleGenerateBug() {
    if (!executionId || generatingBug) return;
    setBugError(null);
    setGeneratingBug(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/ai/generate-bug-report`,
        { executionId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data.success) {
        setBugReport(data.data as IBugReport);
      } else {
        setBugError(data.error ?? 'Failed to generate bug report');
      }
    } catch (err: any) {
      setBugError(err?.response?.data?.error ?? 'Failed to generate bug report');
    } finally {
      setGeneratingBug(false);
    }
  }

  async function handleAnalyzeTest(test: ITestResult, idx: number) {
    if (!executionId || !token) return;
    const key = test.testId || idx;
    setTestAiStates(prev => ({ ...prev, [key]: { status: 'loading' } }));
    try {
      const encodedName = encodeURIComponent(test.name ?? test.testId ?? String(idx));
      const { data } = await axios.post(
        `${API_URL}/api/executions/${executionId}/tests/${encodedName}/analyze`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data.success) {
        setTestAiStates(prev => ({
          ...prev,
          [key]: {
            status: 'done',
            rootCause: data.data.rootCause,
            suggestedFix: data.data.suggestedFix,
          },
        }));
      } else {
        setTestAiStates(prev => ({
          ...prev,
          [key]: { status: 'error', errorMsg: data.error ?? 'Analysis failed' },
        }));
      }
    } catch (err: any) {
      setTestAiStates(prev => ({
        ...prev,
        [key]: {
          status: 'error',
          errorMsg: err?.response?.data?.error ?? 'Analysis failed',
        },
      }));
    }
  }

  const iconBtnBase = 'flex items-center justify-center w-8 h-8 rounded-lg border transition-colors cursor-pointer';

  return (
    <Transition show={isOpen}>
      <Dialog onClose={onClose} aria-labelledby="execution-drawer-title" className="relative z-50">

        {/* ── Backdrop ────────────────────────────────────────────────────── */}
        <TransitionChild
          enter="transition-opacity duration-300 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-200 ease-in"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            data-testid="execution-drawer-backdrop"
            className="fixed inset-0 bg-slate-900/50 dark:bg-black/70"
            aria-hidden="true"
          />
        </TransitionChild>

        {/* ── Slide panel ─────────────────────────────────────────────────── */}
        <div className="fixed inset-0 overflow-hidden">
          <div className="flex h-full items-stretch justify-end">
            <TransitionChild
              enter="transition-transform duration-300 ease-out"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transition-transform duration-200 ease-in"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <DialogPanel data-testid="execution-drawer-panel" className="relative flex h-full flex-col bg-white dark:bg-gh-bg-dark border-l border-slate-200 dark:border-gh-border-dark shadow-2xl w-full max-w-full md:w-[600px] lg:w-[896px]">

                {/* ── Header ────────────────────────────────────────────── */}
                <div data-testid="execution-drawer-header" className="flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-200 dark:border-gh-border-dark shrink-0">
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <h2 id="execution-drawer-title" className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Execution Details
                    </h2>
                    <p className="text-xs font-mono text-slate-500 dark:text-slate-400 truncate">
                      {executionId}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Analyzing spinner */}
                    {execution?.status === 'ANALYZING' && (
                      <div
                        data-testid="execution-drawer-analyzing-spinner"
                        role="status"
                        aria-label="AI Analysis in progress"
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800"
                      >
                        <Loader2 size={12} className="animate-spin" />
                        <span>Analyzing...</span>
                      </div>
                    )}

                    {/* Auto-Generate Bug button — visible only when autoBugGeneration flag is on */}
                    {canGenerateBug && (
                      <button
                        data-testid="execution-drawer-auto-bug-button"
                        type="button"
                        onClick={handleGenerateBug}
                        disabled={generatingBug}
                        aria-label="Generate AI bug report"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 hover:bg-violet-100 dark:hover:bg-violet-950/60 disabled:opacity-50 disabled:cursor-wait transition-colors"
                      >
                        {generatingBug
                          ? <><Loader2 size={13} className="animate-spin" /> Generating…</>
                          : <><Sparkles size={13} /> Auto Bug</>
                        }
                      </button>
                    )}

                    {/* Bug generation error inline (non-modal) */}
                    {bugError && (
                      <span className="text-xs text-red-600 dark:text-red-400 max-w-[140px] truncate" title={bugError}>
                        {bugError}
                      </span>
                    )}

                    {/* Create / View Jira Ticket button — FAILED/ERROR/UNSTABLE */}
                    {execution && (execution.status === 'FAILED' || execution.status === 'ERROR' || execution.status === 'UNSTABLE') && integrations?.jira?.enabled && (() => {
                      const hasTickets = ((execution as any).jiraTickets?.length ?? 0) > 0;
                      return (
                        <button
                          data-testid="execution-drawer-jira-button"
                          onClick={() => setShowJiraModal(true)}
                          title={hasTickets ? `${(execution as any).jiraTickets.length} Jira ticket(s) linked — click to view or create another` : 'Create Jira Ticket'}
                          aria-label={hasTickets ? 'View or create Jira ticket' : 'Create Jira Ticket'}
                          className={`${iconBtnBase} ${hasTickets
                            ? 'text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/40 border-blue-300 dark:border-blue-700 hover:bg-blue-200 dark:hover:bg-blue-900/60'
                            : 'text-blue-500 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-950/50'
                            }`}
                        >
                          <Bug size={16} />
                        </button>
                      );
                    })()}

                    {/* Create Linear Issue button — FAILED/ERROR/UNSTABLE */}
                    {execution && (execution.status === 'FAILED' || execution.status === 'ERROR' || execution.status === 'UNSTABLE') && integrations?.linear?.enabled && (() => {
                      const hasIssues = ((execution as any).linearIssues?.length ?? 0) > 0;
                      return (
                        <button
                          data-testid="execution-drawer-linear-button"
                          onClick={() => setShowLinearModal(true)}
                          title={hasIssues ? `${(execution as any).linearIssues.length} Linear issue(s) linked — click to create another` : 'Create Linear Issue'}
                          aria-label={hasIssues ? 'View or create Linear issue' : 'Create Linear Issue'}
                          className={`${iconBtnBase} ${hasIssues
                            ? 'text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 border-indigo-300 dark:border-indigo-700 hover:bg-indigo-200 dark:hover:bg-indigo-900/60'
                            : 'text-indigo-500 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100 dark:hover:bg-indigo-950/50'
                            }`}
                        >
                          <Zap size={16} />
                        </button>
                      );
                    })()}

                    {/* Create Monday.com Item button — FAILED/ERROR/UNSTABLE */}
                    {execution && (execution.status === 'FAILED' || execution.status === 'ERROR' || execution.status === 'UNSTABLE') && integrations?.monday?.enabled && (() => {
                      const hasItems = ((execution as any).mondayItems?.length ?? 0) > 0;
                      return (
                        <button
                          data-testid="execution-drawer-monday-button"
                          onClick={() => setShowMondayModal(true)}
                          title={hasItems ? `${(execution as any).mondayItems.length} Monday.com item(s) linked — click to create another` : 'Create Monday.com Item'}
                          aria-label={hasItems ? 'View or create Monday.com item' : 'Create Monday.com Item'}
                          className={`${iconBtnBase} ${hasItems
                            ? 'text-rose-700 dark:text-rose-300 bg-rose-100 dark:bg-rose-900/40 border-rose-300 dark:border-rose-700 hover:bg-rose-200 dark:hover:bg-rose-900/60'
                            : 'text-rose-500 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800 hover:bg-rose-100 dark:hover:bg-rose-950/50'
                            }`}
                        >
                          <LayoutGrid size={16} />
                        </button>
                      );
                    })()}

                    {/* Report links */}
                    {execution && isFinished && (
                      <>
                        {areReportsInaccessible ? (
                          <span
                            title="Reports available locally"
                            className="text-[10px] text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded cursor-help"
                          >
                            Local Reports
                          </span>
                        ) : (
                          <>
                            {execution.hasNativeReport === true && (
                              <button
                                data-testid="execution-drawer-html-report-button"
                                type="button"
                                disabled={fetchingReport}
                                title="HTML Report"
                                aria-label="Open HTML Report"
                                onClick={() => handleViewReport(htmlReportUrl)}
                                className={`${iconBtnBase} text-blue-500 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-950/50 disabled:opacity-50 disabled:cursor-wait`}
                              >
                                {fetchingReport ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                              </button>
                            )}
                            {execution.hasAllureReport === true && (
                              <button
                                data-testid="execution-drawer-allure-report-button"
                                type="button"
                                disabled={fetchingReport}
                                title="Allure Report"
                                aria-label="Open Allure Report"
                                onClick={() => handleViewReport(allureReportUrl)}
                                className={`${iconBtnBase} text-emerald-500 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-950/50 disabled:opacity-50 disabled:cursor-wait`}
                              >
                                {fetchingReport ? <Loader2 size={16} className="animate-spin" /> : <BarChart2 size={16} />}
                              </button>
                            )}
                          </>
                        )}
                      </>
                    )}

                    <button
                      data-testid="execution-drawer-close-button"
                      onClick={onClose}
                      aria-label="Close drawer"
                      className="ml-2 flex shrink-0 items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors duration-150 cursor-pointer"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>

                {/* ── Context panels: CI Context + Smart PR (scrollable to protect terminal height) ── */}
                <div className="shrink-0 max-h-[30vh] overflow-y-auto border-b border-slate-200 dark:border-gh-border-dark">

                {/* ── CI Context panel — only for external-ci runs ──────── */}
                {(execution as any)?.source === 'external-ci' && (() => {
                  const ci = (execution as any)?.ingestMeta?.ciContext;
                  const CI_PROVIDER_ICONS: Record<string, React.ElementType> = {
                    github: Github, gitlab: Gitlab, azure: Cloud,
                    jenkins: Cloud, local: Cloud,
                  };
                  const ProviderIcon = ci?.source ? (CI_PROVIDER_ICONS[ci.source] ?? Cloud) : Cloud;
                  const providerLabel: Record<string, string> = {
                    github: 'GitHub', gitlab: 'GitLab', azure: 'Azure DevOps',
                    jenkins: 'Jenkins', local: 'Local',
                  };

                  return (
                    <div data-testid="execution-drawer-ci-context-panel" className="shrink-0 border-b border-violet-100 dark:border-violet-900/50 bg-violet-50/60 dark:bg-violet-950/20">
                      {/* Collapsible header */}
                      <button
                        type="button"
                        data-testid="execution-drawer-ci-context-toggle"
                        onClick={() => setCiContextOpen((v) => !v)}
                        aria-expanded={ciContextOpen}
                        aria-controls="execution-drawer-ci-context-body"
                        className="w-full flex items-center justify-between px-6 py-2.5 cursor-pointer group"
                      >
                        <div className="flex items-center gap-2">
                          <ProviderIcon size={14} className="text-violet-600 dark:text-violet-400 shrink-0" />
                          <span className="text-xs font-semibold text-violet-700 dark:text-violet-300 uppercase tracking-wide">
                            CI Context
                            {ci?.source && (
                              <span className="ml-1.5 font-normal normal-case text-violet-500 dark:text-violet-400">
                                — {providerLabel[ci.source] ?? ci.source}
                              </span>
                            )}
                          </span>
                        </div>
                        {ciContextOpen
                          ? <ChevronDown size={14} className="text-violet-400 dark:text-violet-500 group-hover:text-violet-600 dark:group-hover:text-violet-300 transition-colors" />
                          : <ChevronRight size={14} className="text-violet-400 dark:text-violet-500 group-hover:text-violet-600 dark:group-hover:text-violet-300 transition-colors" />
                        }
                      </button>

                      {/* Collapsible body */}
                      {ciContextOpen && (
                        <div id="execution-drawer-ci-context-body" data-testid="execution-drawer-ci-context-body" className="px-6 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">

                          {ci?.repository && (
                            <div className="flex items-start gap-2 min-w-0">
                              <ProviderIcon size={12} className="text-violet-400 dark:text-violet-500 mt-0.5 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-[10px] font-medium text-violet-400 dark:text-violet-500 uppercase tracking-wide mb-0.5">Repository</p>
                                <p className="text-xs text-slate-700 dark:text-slate-300 font-medium truncate">{ci.repository}</p>
                              </div>
                            </div>
                          )}

                          {ci?.branch && (
                            <div className="flex items-start gap-2 min-w-0">
                              <GitBranch size={12} className="text-violet-400 dark:text-violet-500 mt-0.5 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-[10px] font-medium text-violet-400 dark:text-violet-500 uppercase tracking-wide mb-0.5">Branch</p>
                                <p className="text-xs text-slate-700 dark:text-slate-300 font-mono truncate">{ci.branch}</p>
                              </div>
                            </div>
                          )}

                          {ci?.prNumber && (
                            <div className="flex items-start gap-2 min-w-0">
                              <GitPullRequest size={12} className="text-violet-400 dark:text-violet-500 mt-0.5 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-[10px] font-medium text-violet-400 dark:text-violet-500 uppercase tracking-wide mb-0.5">Pull Request</p>
                                <p className="text-xs text-slate-700 dark:text-slate-300 font-mono">#{ci.prNumber}</p>
                              </div>
                            </div>
                          )}

                          {ci?.commitSha && (
                            <div className="flex items-start gap-2 min-w-0">
                              <GitCommit size={12} className="text-violet-400 dark:text-violet-500 mt-0.5 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-[10px] font-medium text-violet-400 dark:text-violet-500 uppercase tracking-wide mb-0.5">Commit</p>
                                <p className="text-xs text-slate-700 dark:text-slate-300 font-mono truncate" title={ci.commitSha}>
                                  {ci.commitSha.slice(0, 12)}
                                </p>
                              </div>
                            </div>
                          )}

                          {ci?.runUrl && (
                            <div className="flex items-start gap-2 min-w-0 sm:col-span-2">
                              <ExternalLink size={12} className="text-violet-400 dark:text-violet-500 mt-0.5 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-[10px] font-medium text-violet-400 dark:text-violet-500 uppercase tracking-wide mb-0.5">CI Job URL</p>
                                <a
                                  data-testid="execution-drawer-ci-run-url"
                                  href={ci.runUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium truncate block max-w-xs"
                                >
                                  {ci.runUrl}
                                </a>
                              </div>
                            </div>
                          )}

                          {!ci && (
                            <p className="text-xs text-violet-400 dark:text-violet-500 italic sm:col-span-2">
                              No CI context metadata available for this run.
                            </p>
                          )}

                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── Smart PR Context panel ───────────────────────────────────────── */}
                {(execution as any)?.isSmartPR === true && (() => {
                  const ci = (execution as any)?.ciContext;
                  return (
                    <div data-testid="execution-drawer-smart-pr-panel" className="shrink-0 border-b border-violet-100 dark:border-violet-900/50 bg-violet-50/60 dark:bg-violet-950/20">
                      {/* Collapsible header */}
                      <button
                        type="button"
                        data-testid="execution-drawer-smart-pr-toggle"
                        onClick={() => setSmartPrOpen((v) => !v)}
                        aria-expanded={smartPrOpen}
                        aria-controls="execution-drawer-smart-pr-body"
                        className="w-full flex items-center justify-between px-6 py-2.5 cursor-pointer group"
                      >
                        <div className="flex items-center gap-2">
                          <Sparkles size={14} className="text-violet-600 dark:text-violet-400 shrink-0" />
                          <span className="text-xs font-semibold text-violet-700 dark:text-violet-300 uppercase tracking-wide">
                            Smart PR Webhook
                            <span className="ml-1.5 font-normal normal-case text-violet-500 dark:text-violet-400">— AI-Driven Routing</span>
                          </span>
                        </div>
                        {smartPrOpen
                          ? <ChevronDown size={14} className="text-violet-400 dark:text-violet-500 group-hover:text-violet-600 dark:group-hover:text-violet-300 transition-colors" />
                          : <ChevronRight size={14} className="text-violet-400 dark:text-violet-500 group-hover:text-violet-600 dark:group-hover:text-violet-300 transition-colors" />
                        }
                      </button>

                      {/* Collapsible body */}
                      {smartPrOpen && (
                        <div id="execution-drawer-smart-pr-body">
                          {ci && (
                            <div className="px-6 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                              {ci.repository && (
                                <div className="flex items-start gap-2 min-w-0">
                                  <Github size={12} className="text-violet-400 dark:text-violet-500 mt-0.5 shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-[10px] font-medium text-violet-400 dark:text-violet-500 uppercase tracking-wide mb-0.5">Repository</p>
                                    <p className="text-xs text-slate-700 dark:text-slate-300 font-medium truncate">{ci.repository}</p>
                                  </div>
                                </div>
                              )}
                              {ci.branch && (
                                <div className="flex items-start gap-2 min-w-0">
                                  <GitBranch size={12} className="text-violet-400 dark:text-violet-500 mt-0.5 shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-[10px] font-medium text-violet-400 dark:text-violet-500 uppercase tracking-wide mb-0.5">Branch</p>
                                    <p className="text-xs text-slate-700 dark:text-slate-300 font-mono truncate">{ci.branch}</p>
                                  </div>
                                </div>
                              )}
                              {ci.commit && (
                                <div className="flex items-start gap-2 min-w-0">
                                  <GitCommit size={12} className="text-violet-400 dark:text-violet-500 mt-0.5 shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-[10px] font-medium text-violet-400 dark:text-violet-500 uppercase tracking-wide mb-0.5">Commit</p>
                                    <p className="text-xs text-slate-700 dark:text-slate-300 font-mono truncate" title={ci.commit}>{String(ci.commit).slice(0, 12)}</p>
                                  </div>
                                </div>
                              )}
                              {ci.ciJobUrl && (
                                <div className="flex items-start gap-2 min-w-0 sm:col-span-2">
                                  <ExternalLink size={12} className="text-violet-400 dark:text-violet-500 mt-0.5 shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-[10px] font-medium text-violet-400 dark:text-violet-500 uppercase tracking-wide mb-0.5">Pull Request</p>
                                    <a
                                      href={ci.ciJobUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium truncate block max-w-xs"
                                    >
                                      {ci.ciJobUrl}
                                    </a>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* ── AI Reasoning sub-section ─────────────────────── */}
                          <div
                            data-testid="execution-drawer-ai-reasoning"
                            className="px-6 pb-3 mt-2 border-t border-violet-100 dark:border-violet-900/50 pt-3"
                          >
                            <p className="text-[10px] font-medium text-violet-400 dark:text-violet-500 uppercase tracking-wide mb-1">AI Reasoning</p>
                            <p className="text-xs text-slate-700 dark:text-slate-300 italic bg-violet-50/50 dark:bg-violet-900/20 p-2.5 rounded-md border border-violet-100 dark:border-violet-800">
                              {(execution as any)?.aiReasoning || 'No reasoning provided for this routing decision.'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                </div>{/* end scrollable context panels wrapper */}

                {/* ── Tab bar ───────────────────────────────────────────── */}
                <div role="tablist" data-testid="execution-drawer-tab-bar" className="flex items-center gap-1 px-6 border-b border-slate-200 dark:border-gh-border-dark shrink-0">
                  {visibleTabs.map((tab) => (
                    <button
                      key={tab.id}
                      id={`execution-drawer-tab-${tab.id}`}
                      data-testid={`execution-drawer-tab-${tab.id}`}
                      role="tab"
                      aria-selected={effectiveTab === tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors duration-150 cursor-pointer ${effectiveTab === tab.id
                        ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                        }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* ── Tab content ───────────────────────────────────────── */}
                {/* Terminal manages its own scroll; other tabs own their padding wrapper. */}
                <div data-testid="execution-drawer-tab-content" className="flex-1 overflow-hidden">

                  {effectiveTab === 'terminal' && (
                    <TerminalView
                      output={execution?.output}
                      error={execution?.error}
                    />
                  )}

                  {/* ── Tests Tab (Phase 5: Smart Analytics) ──────────────────── */}
                  {effectiveTab === 'tests' && (() => {
                    const testResults = (execution?.tests ?? []).map((t: any): ITestResult => {
                      if (typeof t === 'string') {
                        return { testId: t, name: t, status: 'failed' } as ITestResult;
                      }
                      return t as ITestResult;
                    });
                    return (
                      <div role="tabpanel" aria-labelledby="execution-drawer-tab-tests" className="h-full overflow-y-auto p-4">
                        {testResults.length === 0 ? (
                          <p className="text-sm text-slate-500 dark:text-slate-400 text-center pt-12">No test results recorded.</p>
                        ) : (
                          <ul className="flex flex-col gap-2">
                            {testResults.map((t, idx) => {
                              const isFailed = t.status !== 'passed' && t.status !== 'skipped';
                              const isQuarantinedFail = isFailed && t.isQuarantined;

                              return (
                                <li
                                  key={t.testId || idx}
                                  className={`flex items-start gap-3 p-3 rounded-lg border transition-colors
                                    ${isQuarantinedFail
                                      ? 'bg-slate-50 dark:bg-gh-bg-subtle-dark border-slate-200 dark:border-gh-border-dark opacity-60'
                                      : isFailed
                                        ? 'bg-rose-50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-900/50'
                                        : t.status === 'skipped'
                                          ? 'bg-slate-50 dark:bg-gh-bg-subtle-dark border-slate-200 dark:border-gh-border-dark'
                                          : 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900/50'
                                    }`}
                                >
                                  {/* Status Dot */}
                                  <span
                                    title={t.status}
                                    className={`mt-0.5 shrink-0 w-2.5 h-2.5 rounded-full
                                      ${t.status === 'passed' ? 'bg-emerald-500' :
                                        t.status === 'skipped' ? 'bg-slate-400' :
                                          isQuarantinedFail ? 'bg-slate-400' :
                                            'bg-rose-500'}`}
                                  />

                                  {/* Details */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center flex-wrap gap-2">
                                      <span className={`text-sm font-mono truncate ${isQuarantinedFail ? 'text-slate-500 dark:text-slate-400 line-through' :
                                        isFailed ? 'text-rose-800 dark:text-rose-300' :
                                          'text-slate-800 dark:text-slate-200'
                                        }`}>
                                        {t.name || t.testId || 'Unknown Test'}
                                      </span>

                                      {/* Performance Degradation Warning */}
                                      {t.performanceDegradation && (
                                        <span
                                          title="Performance Degradation: This test ran significantly slower than its historical average."
                                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800"
                                        >
                                          🐌 Slow
                                        </span>
                                      )}

                                      {/* Quarantine Badge */}
                                      {isQuarantinedFail && (
                                        <span
                                          title="This test is quarantined — its failure is suppressed in CI/CD pipelines."
                                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-fuchsia-100 dark:bg-fuchsia-950/40 text-fuchsia-700 dark:text-fuchsia-400 border border-fuchsia-200 dark:border-fuchsia-800"
                                        >
                                          Ignored (Quarantined)
                                        </span>
                                      )}
                                    </div>

                                    {/* Error message — expandable accordion */}
                                    {t.error && !isQuarantinedFail && (() => {
                                      const key = t.testId || idx;
                                      const isExpanded = expandedErrors.has(key);
                                      const isLong = t.error.length > 120;
                                      return (
                                        <div className="mt-1">
                                          {isLong ? (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                setExpandedErrors(prev => {
                                                  const next = new Set(prev);
                                                  isExpanded ? next.delete(key) : next.add(key);
                                                  return next;
                                                })
                                              }
                                              className="flex items-start gap-1 w-full text-left group"
                                            >
                                              <span className="shrink-0 mt-0.5 text-rose-500 dark:text-rose-400 transition-transform duration-200"
                                                style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                                                <ChevronRight size={12} />
                                              </span>
                                              <span className={`text-xs font-mono text-rose-700 dark:text-rose-400 ${isExpanded ? 'whitespace-pre-wrap break-words' : 'truncate'}`}>
                                                {t.error}
                                              </span>
                                            </button>
                                          ) : (
                                            <p className="text-xs text-rose-700 dark:text-rose-400 font-mono whitespace-pre-wrap break-words">
                                              {t.error}
                                            </p>
                                          )}
                                        </div>
                                      );
                                    })()}

                                    {/* Ask AI ✨ — on-demand per-test analysis */}
                                    {isFailed && !isQuarantinedFail && aiFeatures.autoBugGeneration && (() => {
                                      const key = t.testId || idx;
                                      const aiState = testAiStates[key];

                                      // Prefer a fresh LLM result from local state; fall back to the
                                      // server-persisted analysis returned by the GET /executions API.
                                      const cached = (t as any).analysis as
                                        | { rootCause?: string; suggestedFix?: string }
                                        | undefined;
                                      const displayAnalysis =
                                        aiState?.status === 'done'
                                          ? { rootCause: aiState.rootCause, suggestedFix: aiState.suggestedFix }
                                          : cached?.rootCause || cached?.suggestedFix
                                            ? cached
                                            : null;
                                      const hasAnalysis = !!displayAnalysis;

                                      return (
                                        <div className="mt-2">
                                          {/* Trigger button — "Re-analyze" when cached result already present */}
                                          <button
                                            type="button"
                                            data-testid={`test-analyze-ai-btn-${key}`}
                                            onClick={() => handleAnalyzeTest(t, idx)}
                                            disabled={aiState?.status === 'loading'}
                                            aria-label={`Ask AI to analyze ${t.name ?? t.testId}`}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold
                                              text-violet-700 dark:text-violet-300
                                              bg-violet-50 dark:bg-violet-950/30
                                              border border-violet-200 dark:border-violet-800
                                              hover:bg-violet-100 dark:hover:bg-violet-950/60
                                              disabled:opacity-50 disabled:cursor-wait
                                              transition-colors"
                                          >
                                            {aiState?.status === 'loading'
                                              ? <><Loader2 size={11} className="animate-spin" /> Analyzing…</>
                                              : hasAnalysis
                                                ? <><Sparkles size={11} /> Re-analyze ✨</>
                                                : <><Sparkles size={11} /> Ask AI ✨</>
                                            }
                                          </button>

                                          {/* Inline error */}
                                          {aiState?.status === 'error' && (
                                            <p className="mt-1 text-[11px] text-red-500 dark:text-red-400">
                                              {aiState.errorMsg}
                                            </p>
                                          )}

                                          {/* AI result card — shown for both fresh and server-cached analysis */}
                                          {hasAnalysis && (
                                            <div
                                              data-testid={`test-analyze-ai-result-${key}`}
                                              className="mt-2 rounded-lg border border-violet-200 dark:border-violet-800/70
                                                bg-violet-50/60 dark:bg-violet-950/20 p-3 flex flex-col gap-3"
                                            >
                                              {/* Root Cause */}
                                              <div>
                                                <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider
                                                  text-violet-600 dark:text-violet-400 mb-1">
                                                  <Sparkles size={10} />
                                                  Root Cause
                                                </p>
                                                <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
                                                  {displayAnalysis.rootCause}
                                                </p>
                                              </div>

                                              {/* Divider */}
                                              <div className="border-t border-violet-100 dark:border-violet-800/50" />

                                              {/* Suggested Fix */}
                                              <div>
                                                <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider
                                                  text-violet-600 dark:text-violet-400 mb-1">
                                                  <Zap size={10} />
                                                  Suggested Fix
                                                </p>
                                                <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
                                                  {displayAnalysis.suggestedFix}
                                                </p>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}

                                    {/* Duration */}
                                    {typeof t.duration === 'number' && (
                                      <p className="mt-0.5 text-[10px] text-slate-400 dark:text-slate-500">
                                        {(t.duration / 1000).toFixed(2)}s
                                      </p>
                                    )}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    );
                  })()}

                  {effectiveTab === 'artifacts' && (
                    <div role="tabpanel" aria-labelledby="execution-drawer-tab-artifacts" className="h-full overflow-y-auto p-6">
                      <ArtifactsView artifacts={artifacts} isLoading={artifactsLoading} />
                    </div>
                  )}

                </div>

              </DialogPanel>
            </TransitionChild>
          </div>
        </div>

      </Dialog>

      {showJiraModal && execution && (
        <CreateJiraTicketModal
          execution={execution}
          onClose={() => setShowJiraModal(false)}
        />
      )}

      {showLinearModal && execution && (
        <CreateLinearIssueModal
          execution={execution}
          onClose={() => setShowLinearModal(false)}
        />
      )}

      {showMondayModal && execution && (
        <CreateMondayItemModal
          execution={execution}
          onClose={() => setShowMondayModal(false)}
        />
      )}

      {bugReport && execution && (
        <AutoBugModal
          execution={execution}
          bugReport={bugReport}
          onClose={() => setBugReport(null)}
        />
      )}
    </Transition>
  );
}
